import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { FormBuilder, Validators, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { NexusComponent } from '../../../core/components/nexus/nexus.component';
import * as moment from 'moment';
import { NexusFormResultModel } from '../../../core/models/nexus-form-result.model';

@Component({
    selector: 'app-credit-reporting-form',
    templateUrl: './credit-reporting-form.component.html',
    styleUrls: ['./credit-reporting-form.component.scss'],
    standalone: false
})
export class CreditReportingFormComponent extends NexusComponent implements OnInit, OnChanges {
  @Input() credit_reporting_model: any; // Should be proper type
  @Input() header_text = 'Would you like to use Credit Reporting to help build your credit?';
  @Output() modelChanged = new EventEmitter<any>();
  @Output() formValid = new EventEmitter<boolean>();

  public previous_credit_reporting: boolean;
  public credit_reporting_result_model: NexusFormResultModel = new NexusFormResultModel();
  public credit_reporting_form = new UntypedFormGroup({
    credit_reporting_enabled: new UntypedFormControl()
  });
  public credit_reporting_enabled_form = new UntypedFormGroup({
    birth_date: new UntypedFormControl('', [Validators.required, Validators.minLength(8)]),
    legal_first_name: new UntypedFormControl('', [Validators.required, this.ValidateLegalName]),
    legal_middle_name: new UntypedFormControl('', [this.ValidateLegalName]),
    legal_last_name: new UntypedFormControl('', [Validators.required, Validators.minLength(2), this.ValidateLegalName])
  });

  public birthdate_valid = true;
  public legal_name_valid = false;
  private forms_initialized = false;
  private birth_date_invalid = false;

  // Store deep copy of original model to restore when credit reporting is disabled
  private original_model: any;

  constructor() {
    super();
  }

  ngOnInit() {
    this.initializeForms();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['credit_reporting_model'] && changes['credit_reporting_model'].currentValue) {
      this.initializeForms();
    }
  }

  private initializeForms() {
    if (this.credit_reporting_model && !this.forms_initialized) {
      this.forms_initialized = true;
      this.previous_credit_reporting = this.credit_reporting_model.credit_reporting.credit_reporting_enabled;

      // Store deep copy of original model to restore when credit reporting is disabled
      this.original_model = JSON.parse(JSON.stringify(this.credit_reporting_model));

      this.credit_reporting_form.patchValue(this.credit_reporting_model.credit_reporting);
      this.credit_reporting_enabled_form.patchValue(this.credit_reporting_model.credit_reporting);

      // Emit initial validity
      this.formValid.emit(this.credit_reporting_model.credit_reporting.credit_reporting_enabled ? this.credit_reporting_enabled_form.valid : true);

      this.credit_reporting_form.valueChanges.subscribe((form: any) => {
        this.PopulateModelFromForm(form, this.credit_reporting_model.credit_reporting);
        this.modelChanged.emit(this.credit_reporting_model);
        this.formValid.emit(this.credit_reporting_model.credit_reporting.credit_reporting_enabled ? this.credit_reporting_enabled_form.valid : true);
      });
      this.credit_reporting_enabled_form.valueChanges.subscribe((form: any) => {
        this.PopulateModelFromForm(form, this.credit_reporting_model.credit_reporting);
        this.modelChanged.emit(this.credit_reporting_model);
        this.formValid.emit(this.credit_reporting_model.credit_reporting.credit_reporting_enabled ? this.credit_reporting_enabled_form.valid : true);
      });

      this.credit_reporting_enabled_form.get('birth_date').valueChanges.subscribe(date => {
        const max = moment().subtract(17, 'years');
        const min = moment([1900, 1, 1]);
        this.birthdate_valid = date > min && date < max;
      });

      this.credit_reporting_enabled_form.valueChanges.subscribe(value => {
        this.legal_name_valid = this.credit_reporting_enabled_form.get('legal_first_name').valid &&
                                this.credit_reporting_enabled_form.get('legal_middle_name').valid &&
                                this.credit_reporting_enabled_form.get('legal_last_name').valid;
      });
    }
  }

  get showlegalNameError(){
    return this.credit_reporting_enabled_form.get('legal_first_name')?.hasError('invalidLegalName')
            || this.credit_reporting_enabled_form.get('legal_middle_name')?.hasError('invalidLegalName')
            || this.credit_reporting_enabled_form.get('legal_last_name')?.hasError('invalidLegalName');
  }
  get showlegallastNameMinlengthError(){
    return this.credit_reporting_enabled_form.get('legal_last_name')?.hasError('minlength');
  }
  get birthDateErrorMessage(): string {
    const birthDateControl = this.credit_reporting_enabled_form.get('birth_date');

    // If the date input component indicates an invalid date, show invalid message
    if (this.birth_date_invalid) {
      return 'Please select a valid date.';
    }

    // If control has required error, show required message
    if (birthDateControl.errors?.['required']) {
      return 'Please enter your birth date.';
    }

    return '';
  }

  get legalNameErrorMessage(): string {
    const messages: string[] = [];

    if (this.showlegalNameError) {
      messages.push('Legal name may only contain letters, spaces, hyphens, and apostrophes.');
    }

    if (this.showlegallastNameMinlengthError) {
      messages.push('Last Name may not be an initial.');
    }

    if (!this.credit_reporting_enabled_form.valid && !this.legal_name_valid && !this.showlegalNameError && !this.showlegallastNameMinlengthError) {
      messages.push('Please enter your Legal Name.');
    }

    return messages.join(' ');
  }

  get monthlyCreditReportingAmount(): number {
    return this.credit_reporting_model?.credit_reporting?.credit_reporting_amount;
  }

  onBirthDateInvalidChange(isInvalid: boolean) {
    this.birth_date_invalid = isInvalid;
  }

  creditReportingToggled() {
    const wasEnabled = this.credit_reporting_model.credit_reporting.credit_reporting_enabled;
    this.credit_reporting_model.credit_reporting.credit_reporting_enabled = !this.credit_reporting_model.credit_reporting.credit_reporting_enabled;

    // If toggling to disabled, restore original values from deep copy
    if (wasEnabled && !this.credit_reporting_model.credit_reporting.credit_reporting_enabled) {
      this.credit_reporting_model.credit_reporting.birth_date = this.original_model.credit_reporting.birth_date;
      this.credit_reporting_model.credit_reporting.legal_first_name = this.original_model.credit_reporting.legal_first_name;
      this.credit_reporting_model.credit_reporting.legal_middle_name = this.original_model.credit_reporting.legal_middle_name;
      this.credit_reporting_model.credit_reporting.legal_last_name = this.original_model.credit_reporting.legal_last_name;

      // Update the form with restored values
      this.credit_reporting_enabled_form.patchValue({
        birth_date: this.original_model.credit_reporting.birth_date,
        legal_first_name: this.original_model.credit_reporting.legal_first_name,
        legal_middle_name: this.original_model.credit_reporting.legal_middle_name,
        legal_last_name: this.original_model.credit_reporting.legal_last_name
      });
    }

    this.modelChanged.emit(this.credit_reporting_model);
    this.formValid.emit(this.credit_reporting_model.credit_reporting.credit_reporting_enabled ? this.credit_reporting_enabled_form.valid : true);
  }
}
